import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/DoneAll';
import MessageIcon from '@mui/icons-material/Message';
import PaymentIcon from '@mui/icons-material/Payment';
import InventoryIcon from '@mui/icons-material/Inventory2';
import WarningIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const TYPE_CONFIG = {
  // Backend event types (notifications v1 hooks)
  inbound_sms:     { icon: MessageIcon,      color: '#8b5cf6', label: 'SMS Received' },
  quote_accepted:  { icon: RequestQuoteIcon, color: '#10b981', label: 'Quote Accepted' },
  quote_declined:  { icon: CancelIcon,       color: '#ef4444', label: 'Quote Declined' },
  contract_signed: { icon: DescriptionIcon,  color: '#0ea5e9', label: 'Contract Signed' },
  // Legacy / future types
  sms_sent:       { icon: MessageIcon,    color: '#3b82f6', label: 'SMS Sent' },
  sms_delivered:  { icon: MessageIcon,    color: '#10b981', label: 'SMS Delivered' },
  sms_received:   { icon: MessageIcon,    color: '#8b5cf6', label: 'SMS Received' },
  payment_due:    { icon: PaymentIcon,    color: '#f59e0b', label: 'Payment Reminder' },
  booking_created:{ icon: InventoryIcon,  color: '#06b6d4', label: 'Booking Created' },
  return_overdue: { icon: WarningIcon,    color: '#ef4444', label: 'Return Overdue' },
  order_status:   { icon: SwapHorizIcon,  color: '#6366f1', label: 'Order Updated' },
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${apiUrl}/v1/notifications`, {
        headers: { 'x-auth-token': token },
        params: { limit: 20 },
      });
      const data = res.data?.data || res.data || [];
      setNotifications(Array.isArray(data) ? data : []);
      setUnreadCount(Array.isArray(data) ? data.filter(n => !n.read).length : 0);
    } catch {
      // Silently fail — notifications are non-critical
    }
  }, []);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${apiUrl}/v1/notifications/read-all`, {}, {
        headers: { 'x-auth-token': token },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const markOneRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${apiUrl}/v1/notifications/${id}/read`, {}, {
        headers: { 'x-auth-token': token },
      });
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  };

  const dismissOne = async (id, event) => {
    event.stopPropagation(); // don't trigger the row's click handler
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/v1/notifications/${id}`, {
        headers: { 'x-auth-token': token },
      });
      const removed = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (removed && !removed.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) markOneRead(notification.id);
    if (notification.link_url) {
      handleClose();
      navigate(notification.link_url);
    }
  };

  const clearAll = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/v1/notifications/clear-all`, {
        headers: { 'x-auth-token': token },
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    fetchNotifications();
    pollingRef.current = setInterval(fetchNotifications, 30000); // poll every 30s
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchNotifications]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          sx={{ ml: 0.5 }}
          aria-label={`${unreadCount} unread notifications`}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 16,
                height: 16,
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              width: 380,
              maxHeight: 520,
              overflow: 'hidden',
              borderRadius: 2,
              mt: 1,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
            {unreadCount > 0 && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>
                {unreadCount} unread
              </Box>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={markAllRead} sx={{ color: 'primary.main' }}>
                  <MarkEmailReadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                onClick={clearAll}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  minWidth: 'auto',
                  px: 1,
                  ml: 0.5,
                  '&:hover': { color: 'error.main', backgroundColor: (t) => alpha(t.palette.error.main, 0.06) },
                }}
              >
                Clear all
              </Button>
            )}
          </Box>
        </Box>

        {/* Notification List */}
        <Box sx={{ overflowY: 'auto', maxHeight: 420 }}>
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Box key={i} sx={{ px: 2, py: 1.5 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Skeleton width="60%" sx={{ mt: -3.5, ml: 5.5 }} />
                <Skeleton width="85%" sx={{ mt: 0.5, ml: 5.5 }} />
              </Box>
            ))
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <NotificationsIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2">No notifications yet</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => {
                const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.sms_sent;
                const Icon = config.icon;
                return (
                  <React.Fragment key={notification.id}>
                    <ListItemButton
                      alignItems="flex-start"
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: notification.read ? 'transparent' : (t) => alpha(t.palette.primary.main, 0.04),
                        '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.08) },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: alpha(config.color, 0.12),
                            color: config.color,
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={notification.read ? 400 : 700} sx={{ flex: 1 }}>
                              {config.label}
                            </Typography>
                            {!notification.read && (
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  backgroundColor: 'primary.main',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 0.25 }}>
                              {notification.title || notification.message || notification.body || notification.body_text}
                            </Typography>
                            {notification.customer_name && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {notification.customer_name} · {notification.phone_number}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                              {formatRelativeTime(notification.created_at || notification.createdAt)}
                            </Typography>
                          </>
                        }
                      />
                      <IconButton
                        className="dismiss-btn"
                        size="small"
                        onClick={(e) => dismissOne(notification.id, e)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: 'text.disabled',
                          transition: 'opacity 0.15s, color 0.15s',
                          '&:hover': { color: 'error.main', backgroundColor: (t) => alpha(t.palette.error.main, 0.08) },
                        }}
                        aria-label="Dismiss notification"
                      >
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </ListItemButton>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              onClick={handleClose}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Close
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
